import React from "react";

/* ────────────────────────────────────────────────────────────────────────
   ViewSalary — full, type-aware salary detail view.

   The salary API now returns a discriminated payload: the employment type is
   given by `salaryStructure.structureType` and the pay breakdown lives in ONE
   of three sibling objects — `regular` (permanent), `contractor` (GST / non-GST
   contract) or `trainee` (stipend). Only the object matching the type is
   populated; the others are null.

   This module owns the salary-view domain: the shared TypeScript types, the
   formatting helpers, and the presentational component. `SalaryDetails.tsx`
   imports the types/helpers for its listing table and renders <SalaryDetailView>
   inside the "View" modal.
   ──────────────────────────────────────────────────────────────────────── */

// ─── Types ──────────────────────────────────────────────────────────────────

export type StructureType =
  | "PERMANENT_EMPLOYEE"
  | "CONTRACT_EMPLOYEE"
  | "TRAINEE"
  | string;

// The salary structure (template) attached to the employee. It carries fields
// for every employment type; only the ones relevant to `structureType` are set.
export interface SalaryStructure {
  structureName: string;
  description?: string | null;
  structureType?: StructureType | null;

  // Permanent-employee percentages / config
  taxOnProfession: number | null;
  basicSalaryPercentage: number | null;
  fbtPercentage: number | null;
  hraPercentage: number | null;
  variableAllPercentage: number | null;
  halfYearBonusPercentage: number | null;
  performanceBonusPercentage: number | null;
  providentFundEmployeePercentage: number | null;
  providentFundEmployerPercentage: number | null;
  conveyanceAllowance: number | null;
  conveyanceIsPercentage: boolean | null;
  esiEmployerPercentage: number | null;
  esiEmployeePercentage: number | null;
  pfApplicable: boolean | null;
  pfWageCeilingApplicable: boolean | null;
  esiApplicable: boolean | null;
  profTaxApplicable: boolean | null;
  medicalInsuranceMonthly: number | null;
  medicalInsuranceIsPercentage: boolean | null;
  medicalAllowance: number | null;
  medicalAllowanceIsPercentage: boolean | null;
  leaMonthly: number | null;
  leaIsPercentage: boolean | null;
  leaDays: number | null;
  annualBonusPercentage: number | null;
  annualBonusIsPercentage: boolean | null;
  gratuityApplicable: boolean | null;
  noidaPtApplicable: boolean | null;

  // Contract-employee (GST / non-GST) config
  gstRequired: boolean | null;
  gstContractValue: number | null;
  gstMonthlyPayout: number | null;
  gstTdsRate: number | null;
  gstPercentage: number | null;
  gstAmount: number | null;
  nonGstContractValue: number | null;
  nonGstMonthlyPayout: number | null;
  nonGstTdsRate: number | null;

  // Trainee / part-time config
  partTimeContractValue: number | null;
  partTimeMonthlyPayout: number | null;
  partTimePeriod: string | null;
}

// Permanent employee pay breakdown (`regular`).
export interface RegularSalaryDetails {
  annualCtc: number | null;
  grossSalary: number | null;
  basicSalary: number | null;
  hra: number | null;
  variableAllowance: number | null;
  halfYearBonus: number | null;
  performanceBonus: number | null;
  conveyanceAllowance: number | null;
  medicalAllowance: number | null;
  medicalInsurance: number | null;
  leaAllowance: number | null;
  annualBonus: number | null;
  gratuity: number | null;
  fbtAllowance: number | null;
  providentFundEmployee: number | null;
  providentFundEmployer: number | null;
  esiEmployee: number | null;
  esiEmployer: number | null;
  taxOnProfession: number | null;
}

// Contract employee pay breakdown (`contractor`).
export interface ContractorSalaryDetails {
  gstRequired: boolean | null;
  gstPercentage: number | null;
  gstMonthlyPayout: number | null;
  gstTdsRate: number | null;
  gstContractValue: number | null;
  gstAmount: number | null;
  nonGstMonthlyPayout: number | null;
  nonGstTdsRate: number | null;
  nonGstContractValue: number | null;
  grossSalary: number | null;
}

// Trainee pay breakdown (`trainee`). Not present in the current API payload —
// modelled here from the backend TraineeSalaryDetails DTO for forward support.
export interface TraineeSalaryDetails {
  monthlyStipend: number | null;
  trainingPeriod: string | null;
  partTimeContractValue: number | null;
  grossSalary: number | null;
}

// One employee's salary record.
export interface SalaryFormValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  id: number;
  employeeEmail: string;
  userName: string;
  modifiedDate: number;
  tds: number | null;
  tdsOverride: number | null;
  salaryAdvance: number | null;
  netTakeHome: number | null;
  mediclaimSumAssured: number | null;
  appraisalType: string | null;
  appraisalPercentage: number | null;
  salaryStructure: SalaryStructure;
  regular: RegularSalaryDetails | null;
  contractor: ContractorSalaryDetails | null;
  trainee: TraineeSalaryDetails | null;
}

// ─── Formatting helpers (shared with the listing table) ─────────────────────

export const inr = (value: number | null | undefined): string => {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const pct = (value: number | null | undefined): string =>
  value == null ? "—" : `${value}%`;

export const epochToDisplay = (ms: number | null | undefined): string => {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Which pay-breakdown object is in play. Prefer the populated object, falling
// back to the structure type so an empty-but-typed record still renders right.
export type SalaryMode = "regular" | "contract" | "trainee";

export const resolveSalaryMode = (row: SalaryFormValues): SalaryMode => {
  if (row?.contractor) return "contract";
  if (row?.trainee) return "trainee";
  if (row?.regular) return "regular";
  const type = row?.salaryStructure?.structureType;
  if (type === "CONTRACT_EMPLOYEE") return "contract";
  if (type === "TRAINEE") return "trainee";
  return "regular";
};

// Friendly label for the employment type — used by the table badge + header.
export const structureTypeLabel = (row: SalaryFormValues): string => {
  switch (resolveSalaryMode(row)) {
    case "contract":
      return "Contractor";
    case "trainee":
      return "Trainee";
    default:
      return "Permanent";
  }
};

// Gross salary is present on every breakdown type — the one universally
// comparable figure for the listing table.
export const getGrossSalary = (row: SalaryFormValues): number | null =>
  row?.regular?.grossSalary ??
  row?.contractor?.grossSalary ??
  row?.trainee?.grossSalary ??
  null;

// The headline "contract value" / CTC, whichever applies to the type.
export const getContractValue = (row: SalaryFormValues): number | null =>
  row?.regular?.annualCtc ??
  row?.contractor?.gstContractValue ??
  row?.contractor?.nonGstContractValue ??
  row?.trainee?.partTimeContractValue ??
  null;

// ─── Presentational building blocks ─────────────────────────────────────────

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, highlight }) => (
  <div className={`flex justify-between items-center gap-4 py-2 border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${highlight ? "font-semibold" : ""}`}>
    <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
    <span className={`text-sm text-right tabular-nums ${highlight ? "text-primary font-semibold" : "text-slate-900 dark:text-white font-medium"}`}>{value}</span>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

const Section: React.FC<SectionProps> = ({ title, children, className }) => (
  <div className={`mb-5 ${className ?? ""}`}>
    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 px-1">{title}</h4>
    <div className="bg-slate-50 dark:bg-gray-900 rounded-lg px-4 py-1">{children}</div>
  </div>
);

const BoolBadge: React.FC<{ value: boolean | null | undefined }> = ({ value }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${value ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>
    {value ? "Yes" : "No"}
  </span>
);

const BadgeRow = ({ label, value }: { label: string; value: boolean | null | undefined }) => (
  <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700 last:border-b-0">
    <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
    <BoolBadge value={value} />
  </div>
);

// Coloured chip for the employment type in the detail header.
const TypeBadge: React.FC<{ mode: SalaryMode }> = ({ mode }) => {
  const map: Record<SalaryMode, { label: string; cls: string }> = {
    regular: { label: "Permanent", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    contract: { label: "Contractor", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
    trainee: { label: "Trainee", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  };
  const { label, cls } = map[mode];
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
};

// Compact KPI card for the summary strip.
const StatCard: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm">
    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
    <p className={`mt-1 text-lg font-bold tabular-nums ${accent ?? "text-slate-900 dark:text-white"}`}>{value}</p>
  </div>
);

// ─── Type-specific breakdown blocks ─────────────────────────────────────────

const RegularBreakdown: React.FC<{ data: SalaryFormValues }> = ({ data }) => {
  const r = data.regular ?? ({} as RegularSalaryDetails);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6">
      {/* Column 1: Annual comp & deductions */}
      <div>
        <Section title="Annual Compensation">
          <DetailRow label="Annual CTC" value={inr(r.annualCtc)} />
          <DetailRow label="Gross Salary" value={inr(r.grossSalary)} />
          <DetailRow label="Net Take Home" value={inr(data.netTakeHome)} highlight />
        </Section>

        <Section title="Deductions">
          <DetailRow label="PF (Employee)" value={inr(r.providentFundEmployee)} />
          <DetailRow label="ESI (Employee)" value={inr(r.esiEmployee)} />
          <DetailRow label="Prof Tax" value={inr(r.taxOnProfession)} />
          <DetailRow label="TDS" value={inr(data.tds)} />
          <DetailRow label="TDS Override" value={inr(data.tdsOverride)} />
          <DetailRow label="Salary Advance" value={inr(data.salaryAdvance)} />
        </Section>
      </div>

      {/* Column 2: Earnings */}
      <div>
        <Section title="Earnings">
          <DetailRow label="Basic Salary" value={inr(r.basicSalary)} />
          <DetailRow label="HRA" value={inr(r.hra)} />
          <DetailRow label="Variable Allowance" value={inr(r.variableAllowance)} />
          <DetailRow label="Conveyance Allowance" value={inr(r.conveyanceAllowance)} />
          <DetailRow label="Medical Allowance" value={inr(r.medicalAllowance)} />
          <DetailRow label="Medical Insurance" value={inr(r.medicalInsurance)} />
          <DetailRow label="LEA Allowance" value={inr(r.leaAllowance)} />
          <DetailRow label="Annual Bonus" value={inr(r.annualBonus)} />
          <DetailRow label="Half Year Bonus" value={inr(r.halfYearBonus)} />
          <DetailRow label="Performance Bonus" value={inr(r.performanceBonus)} />
          <DetailRow label="FBT Allowance" value={inr(r.fbtAllowance)} />
        </Section>
      </div>

      {/* Column 3: Employer contributions & appraisal */}
      <div>
        <Section title="Employer Contributions">
          <DetailRow label="PF (Employer)" value={inr(r.providentFundEmployer)} />
          <DetailRow label="ESI (Employer)" value={inr(r.esiEmployer)} />
          <DetailRow label="Gratuity" value={inr(r.gratuity)} />
          <DetailRow label="Mediclaim Sum Assured" value={inr(data.mediclaimSumAssured)} />
        </Section>

        {data.appraisalType && (
          <Section title="Appraisal">
            <DetailRow label="Type" value={data.appraisalType ?? "—"} />
            <DetailRow label="Percentage" value={pct(data.appraisalPercentage)} />
          </Section>
        )}
      </div>
    </div>
  );
};

const ContractorBreakdown: React.FC<{ data: SalaryFormValues }> = ({ data }) => {
  const c = data.contractor ?? ({} as ContractorSalaryDetails);
  const hasGst = c.gstRequired === true;
  const hasNonGst =
    c.nonGstContractValue != null || c.nonGstMonthlyPayout != null || c.nonGstTdsRate != null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6">
      {/* Column 1: Contract summary + deductions */}
      <div>
        <Section title="Contract Summary">
          <DetailRow label="GST Required" value={<BoolBadge value={c.gstRequired} />} />
          <DetailRow label="Gross Salary" value={inr(c.grossSalary)} />
          <DetailRow label="Net Take Home" value={inr(data.netTakeHome)} highlight />
        </Section>

        <Section title="Deductions">
          <DetailRow label="TDS" value={inr(data.tds)} />
          <DetailRow label="TDS Override" value={inr(data.tdsOverride)} />
          <DetailRow label="Salary Advance" value={inr(data.salaryAdvance)} />
        </Section>
      </div>

      {/* Column 2: GST details */}
      <div>
        <Section title="GST Details">
          <DetailRow label="GST %" value={pct(c.gstPercentage)} />
          <DetailRow label="GST Contract Value" value={inr(c.gstContractValue)} />
          <DetailRow label="GST Monthly Payout" value={inr(c.gstMonthlyPayout)} />
          <DetailRow label="GST TDS Rate" value={pct(c.gstTdsRate)} />
          <DetailRow label="GST Amount" value={inr(c.gstAmount)} highlight={hasGst} />
        </Section>
      </div>

      {/* Column 3: Non-GST details (only when the contract has them) */}
      <div>
        {hasNonGst ? (
          <Section title="Non-GST Details">
            <DetailRow label="Non-GST Contract Value" value={inr(c.nonGstContractValue)} />
            <DetailRow label="Non-GST Monthly Payout" value={inr(c.nonGstMonthlyPayout)} />
            <DetailRow label="Non-GST TDS Rate" value={pct(c.nonGstTdsRate)} />
          </Section>
        ) : (
          <Section title="Non-GST Details">
            <p className="py-3 text-sm text-slate-400 dark:text-slate-500">
              Not applicable for this contract.
            </p>
          </Section>
        )}
      </div>
    </div>
  );
};

const TraineeBreakdown: React.FC<{ data: SalaryFormValues }> = ({ data }) => {
  const t = data.trainee ?? ({} as TraineeSalaryDetails);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6">
      <div>
        <Section title="Stipend">
          <DetailRow label="Monthly Stipend" value={inr(t.monthlyStipend)} />
          <DetailRow label="Gross Salary" value={inr(t.grossSalary)} />
          <DetailRow label="Net Take Home" value={inr(data.netTakeHome)} highlight />
        </Section>
      </div>
      <div>
        <Section title="Engagement">
          <DetailRow label="Training Period" value={t.trainingPeriod ?? "—"} />
          <DetailRow label="Part-Time Contract Value" value={inr(t.partTimeContractValue)} />
        </Section>
      </div>
      <div>
        <Section title="Deductions">
          <DetailRow label="TDS" value={inr(data.tds)} />
          <DetailRow label="TDS Override" value={inr(data.tdsOverride)} />
          <DetailRow label="Salary Advance" value={inr(data.salaryAdvance)} />
        </Section>
      </div>
    </div>
  );
};

// ─── Salary structure (template) block — adapts to the employment type ──────

const StructureDetails: React.FC<{ mode: SalaryMode; s: SalaryStructure }> = ({ mode, s }) => {
  if (mode === "contract") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6">
        <div>
          {s.description && <DetailRow label="Description" value={s.description} />}
          <BadgeRow label="GST Required" value={s.gstRequired} />
          <DetailRow label="GST %" value={pct(s.gstPercentage)} />
          <DetailRow label="GST Amount" value={inr(s.gstAmount)} />
        </div>
        <div>
          <DetailRow label="GST Contract Value" value={inr(s.gstContractValue)} />
          <DetailRow label="GST Monthly Payout" value={inr(s.gstMonthlyPayout)} />
          <DetailRow label="GST TDS Rate" value={pct(s.gstTdsRate)} />
        </div>
        <div>
          <DetailRow label="Non-GST Contract Value" value={inr(s.nonGstContractValue)} />
          <DetailRow label="Non-GST Monthly Payout" value={inr(s.nonGstMonthlyPayout)} />
          <DetailRow label="Non-GST TDS Rate" value={pct(s.nonGstTdsRate)} />
        </div>
      </div>
    );
  }

  if (mode === "trainee") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6">
        <div>
          {s.description && <DetailRow label="Description" value={s.description} />}
          <DetailRow label="Part-Time Contract Value" value={inr(s.partTimeContractValue)} />
        </div>
        <div>
          <DetailRow label="Part-Time Monthly Payout" value={inr(s.partTimeMonthlyPayout)} />
        </div>
        <div>
          <DetailRow label="Part-Time Period" value={s.partTimePeriod ?? "—"} />
        </div>
      </div>
    );
  }

  // Permanent employee — percentages, allowances and applicability flags.
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6">
      <div>
        {s.description && <DetailRow label="Description" value={s.description} />}
        <DetailRow label="Basic %" value={pct(s.basicSalaryPercentage)} />
        <DetailRow label="HRA %" value={pct(s.hraPercentage)} />
        <DetailRow label="Variable %" value={pct(s.variableAllPercentage)} />
        <DetailRow
          label="Annual Bonus"
          value={s.annualBonusIsPercentage !== false ? pct(s.annualBonusPercentage) : inr(s.annualBonusPercentage)}
        />
        <DetailRow label="Half Year Bonus %" value={pct(s.halfYearBonusPercentage)} />
        <DetailRow label="Performance Bonus %" value={pct(s.performanceBonusPercentage)} />
      </div>
      <div>
        <DetailRow label="FBT %" value={pct(s.fbtPercentage)} />
        <DetailRow
          label={s.conveyanceIsPercentage ? "Conveyance (% of Basic)" : "Conveyance (₹)"}
          value={s.conveyanceIsPercentage ? pct(s.conveyanceAllowance) : inr(s.conveyanceAllowance)}
        />
        <DetailRow
          label={s.medicalAllowanceIsPercentage ? "Medical Allowance (% of Basic)" : "Medical Allowance (₹)"}
          value={s.medicalAllowanceIsPercentage ? pct(s.medicalAllowance) : inr(s.medicalAllowance)}
        />
        <DetailRow
          label={s.medicalInsuranceIsPercentage ? "Medical Insurance (% of Basic)" : "Medical Insurance / mo"}
          value={s.medicalInsuranceIsPercentage ? pct(s.medicalInsuranceMonthly) : inr(s.medicalInsuranceMonthly)}
        />
        <DetailRow
          label={s.leaIsPercentage ? "LEA (% of Basic)" : "LEA / mo"}
          value={s.leaIsPercentage ? pct(s.leaMonthly) : inr(s.leaMonthly)}
        />
        <DetailRow label="LEA Days" value={s.leaDays ?? "—"} />
        <DetailRow label="Prof Tax (₹)" value={inr(s.taxOnProfession)} />
      </div>
      <div>
        <DetailRow label="PF Employee %" value={pct(s.providentFundEmployeePercentage)} />
        <DetailRow label="PF Employer %" value={pct(s.providentFundEmployerPercentage)} />
        <DetailRow label="ESI Employee %" value={pct(s.esiEmployeePercentage)} />
        <DetailRow label="ESI Employer %" value={pct(s.esiEmployerPercentage)} />
        <BadgeRow label="PF Applicable" value={s.pfApplicable} />
        <BadgeRow label="ESI Applicable" value={s.esiApplicable} />
        <BadgeRow label="Prof Tax Applicable" value={s.profTaxApplicable} />
        <BadgeRow label="Gratuity Applicable" value={s.gratuityApplicable} />
        <BadgeRow label="Noida PT Applicable" value={s.noidaPtApplicable} />
      </div>
    </div>
  );
};

// ─── Main view ──────────────────────────────────────────────────────────────

export interface SalaryDetailViewProps {
  data: SalaryFormValues;
}

export const SalaryDetailView: React.FC<SalaryDetailViewProps> = ({ data }) => {
  const s = data.salaryStructure;
  const mode = resolveSalaryMode(data);
  const gross = getGrossSalary(data);
  const contractValue = getContractValue(data);

  return (
    <div className="bg-white dark:bg-gray-800 p-4">
      {/* Employee summary header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 p-4 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-lg font-bold text-slate-900 dark:text-white truncate">{data.userName || "—"}</p>
            <TypeBadge mode={mode} />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{data.employeeEmail}</p>
          <p className="text-xs text-slate-400 mt-0.5">Last Modified: {epochToDisplay(data.modifiedDate)}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Net Take Home</p>
          <p className="text-2xl font-bold text-primary tabular-nums">{inr(data.netTakeHome)}</p>
        </div>
      </div>

      {/* KPI strip — the headline figures at a glance */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label={mode === "regular" ? "Annual CTC" : "Contract Value"} value={inr(contractValue)} />
        <StatCard label="Gross Salary" value={inr(gross)} />
        <StatCard label="Net Take Home" value={inr(data.netTakeHome)} accent="text-primary" />
        <StatCard label="TDS" value={inr(data.tds)} accent="text-rose-600 dark:text-rose-400" />
      </div>

      {/* Type-specific breakdown */}
      {mode === "contract" ? (
        <ContractorBreakdown data={data} />
      ) : mode === "trainee" ? (
        <TraineeBreakdown data={data} />
      ) : (
        <RegularBreakdown data={data} />
      )}

      {/* Salary structure (template) */}
      {s && (
        <Section title={`Salary Structure — ${s.structureName}`}>
          <StructureDetails mode={mode} s={s} />
        </Section>
      )}
    </div>
  );
};

// Default export: a thin wrapper so the component can also be used standalone
// (e.g. a future routed page) — currently rendered inside the View modal.
const ViewSalary: React.FC<SalaryDetailViewProps> = ({ data }) => <SalaryDetailView data={data} />;

export default ViewSalary;
